/**
 * Watching the browser's microphone and camera grants while a meeting runs.
 *
 * Chrome (and the other Chromium browsers) let someone revoke access from the
 * address-bar / site-settings panel mid-call, WITHOUT reloading the page. The
 * Permissions API's `change` event is the reliable, spec-blessed signal that it
 * happened — it fires immediately for `granted → denied` AND `granted → prompt`,
 * no reload required, even though Chrome also shows its own "reload to apply"
 * banner. (The capture track's `ended`/`mute` is NOT reliable for this across
 * Chromium versions, so the device manager's `NotAllowedError` path is only a
 * fallback; this is the primary trigger.)
 *
 * Deliberately outside React and the stores, like `camera-interruption`: a hook
 * wires `onChange` to the devices/session stores. Everything is injectable so
 * the behaviour can be tested without a real `navigator.permissions`.
 *
 * Two robustness notes baked in:
 *  - Older Firefox throws `TypeError` for the `microphone`/`camera` descriptor
 *    names. That is caught and the kind is simply not watched — the click-time
 *    gate and the capture's own `NotAllowedError` still cover it there.
 *  - WebKit/Safari has open bugs where `change` does not fire on a mid-session
 *    change and the state is only correct on a fresh `query()`. So the watcher
 *    re-queries on window focus and page visibility, not just on `change`.
 */

import type { MediaPermissionState } from '../models/device.model'

export type PermissionKind = 'audio' | 'video'

/** The Permissions API descriptor name behind each capture kind. */
const DESCRIPTOR: Record<PermissionKind, PermissionName> = {
  audio: 'microphone' as PermissionName,
  video: 'camera' as PermissionName,
}

/** The slice of `PermissionStatus` this reads — declared structurally for tests. */
export type WatchedPermissionStatus = {
  state: PermissionState
  addEventListener(type: 'change', listener: () => void): void
  removeEventListener(type: 'change', listener: () => void): void
}

export type PermissionsLike = {
  query(descriptor: { name: PermissionName }): Promise<WatchedPermissionStatus>
}

/** The slice of `window`/`document` this attaches to — both satisfy it, as does a fake. */
export type ListenerTarget = {
  addEventListener(type: string, listener: () => void): void
  removeEventListener(type: string, listener: () => void): void
}

export type PermissionWatcherOptions = {
  /**
   * Fired on every observed TRANSITION — never on the first read, which only
   * seeds the baseline. `previous` is what it changed from.
   */
  onChange: (
    kind: PermissionKind,
    state: MediaPermissionState,
    previous: MediaPermissionState,
  ) => void
  /** Defaults to `navigator.permissions`; injectable for tests. */
  permissions?: PermissionsLike | null
  /** Defaults to `window`; injectable for tests. The focus re-query uses it. */
  win?: ListenerTarget | null
  /** Defaults to `document`; injectable for tests. The visibility re-query uses it. */
  doc?: (ListenerTarget & { visibilityState: string }) | null
}

export type PermissionWatcher = { dispose(): void }

/**
 * Map a Permissions API state onto ours. `MediaPermissionState`'s extra
 * `'error'` is for a capture that failed, never an answer `query()` gives, so
 * anything that is not a firm grant/deny reads as `'prompt'` — the honest
 * "we do not know / the browser will ask".
 */
export function toPermissionState(state: PermissionState): MediaPermissionState {
  return state === 'granted' ? 'granted' : state === 'denied' ? 'denied' : 'prompt'
}

export function createPermissionWatcher(opts: PermissionWatcherOptions): PermissionWatcher {
  const permissions =
    opts.permissions ?? (typeof navigator === 'undefined' ? null : navigator.permissions ?? null)
  const win = opts.win ?? (typeof window === 'undefined' ? null : window)
  const doc = opts.doc ?? (typeof document === 'undefined' ? null : document)

  let disposed = false
  const last: Record<PermissionKind, MediaPermissionState | undefined> = {
    audio: undefined,
    video: undefined,
  }
  const statuses: Partial<Record<PermissionKind, WatchedPermissionStatus>> = {}
  const handlers: Partial<Record<PermissionKind, () => void>> = {}

  const observe = (kind: PermissionKind, state: MediaPermissionState) => {
    const previous = last[kind]
    if (previous === state) return
    last[kind] = state
    // The first read is the baseline, not a transition — seed it silently.
    if (previous === undefined || disposed) return
    opts.onChange(kind, state, previous)
  }

  // Query (or re-query) one kind and (re)attach the change listener to the
  // status object we get back. Re-querying is deliberate: on Safari the cached
  // status.state can be stale, and a fresh query is the only correct read.
  const track = async (kind: PermissionKind) => {
    if (!permissions?.query) return
    let status: WatchedPermissionStatus
    try {
      status = await permissions.query({ name: DESCRIPTOR[kind] })
    } catch {
      // No descriptor for this name (older Firefox). Leave the kind unwatched.
      return
    }
    if (disposed) return
    const old = statuses[kind]
    const oldHandler = handlers[kind]
    if (old && oldHandler && old !== status) old.removeEventListener('change', oldHandler)
    statuses[kind] = status
    if (old !== status || !handlers[kind]) {
      const handler = () => observe(kind, toPermissionState(status.state))
      handlers[kind] = handler
      status.addEventListener('change', handler)
    }
    observe(kind, toPermissionState(status.state))
  }

  const reread = () => {
    void track('audio')
    void track('video')
  }
  const onVisibility = () => {
    if (doc?.visibilityState === 'visible') reread()
  }

  win?.addEventListener('focus', reread)
  doc?.addEventListener('visibilitychange', onVisibility)
  void track('audio')
  void track('video')

  return {
    dispose() {
      if (disposed) return
      disposed = true
      win?.removeEventListener('focus', reread)
      doc?.removeEventListener('visibilitychange', onVisibility)
      ;(['audio', 'video'] as PermissionKind[]).forEach((kind) => {
        const status = statuses[kind]
        const handler = handlers[kind]
        if (status && handler) status.removeEventListener('change', handler)
      })
    },
  }
}
